/**
 * Pricing Page Component
 * Advanced TaaS Pricing Calculator with Suggestions & Anchor Framing
 */

import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Pricing.css';

// --- Icons ---
const CheckIcon = () => (
    <svg className="icon icon-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

const CloseIcon = () => (
    <svg className="icon icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const WarningIcon = () => (
    <svg className="icon icon-warning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

// --- Constants ---
const RATES = {
    JUNIOR: 5900,
    SENIOR: 10900,
};

// Defaults (Anchoring)
const DEFAULTS = {
    JUNIOR: 2,
    SENIOR: 1,
    TENURE: 12
};

// Suggestions
const SUGGESTIONS = [
    {
        id: 'mvp',
        label: 'Minimum Viable Team',
        values: { junior: 1, senior: 1, tenure: 6 }
    },
    {
        id: 'recommended',
        label: 'Recommended',
        isHighlight: true,
        values: { junior: 2, senior: 1, tenure: 12 }
    },
    {
        id: 'scale',
        label: 'Scale Team',
        values: { junior: 3, senior: 2, tenure: 12 }
    }
];

// Discount Logic
const getDiscountInfo = (months) => {
    if (months >= 18) return { percent: 0.10, label: 'Max Value' };
    if (months >= 12) return { percent: 0.08, label: 'Best Value' };
    if (months >= 9) return { percent: 0.05, label: '' };
    if (months >= 6) return { percent: 0.03, label: '' };
    return { percent: 0, label: '' };
};

const formatCurrency = (amount) => {
    // No decimals, thousands separator
    return `RM ${Math.round(amount).toLocaleString()}`;
};

function Pricing() {
    // State
    const [numJuniors, setNumJuniors] = useState(DEFAULTS.JUNIOR);
    const [numSeniors, setNumSeniors] = useState(DEFAULTS.SENIOR);
    const [tenureMonths, setTenureMonths] = useState(DEFAULTS.TENURE);

    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Calculations
    const {
        monthlyFee,
        discountPercent,
        discountLabel,
        discountAmount,
        payableMonthly,
        totalContractValue,
        isValidConfig
    } = useMemo(() => {
        const hasDevelopers = numJuniors > 0 || numSeniors > 0;

        if (!hasDevelopers) {
            return {
                monthlyFee: 0,
                discountPercent: 0,
                discountLabel: '',
                discountAmount: 0,
                payableMonthly: 0,
                totalContractValue: 0,
                isValidConfig: false
            };
        }

        const rawMonthly = (numJuniors * RATES.JUNIOR) + (numSeniors * RATES.SENIOR);
        const { percent, label } = getDiscountInfo(tenureMonths);
        const afterDiscount = rawMonthly * (1 - percent);
        const discountAmount = rawMonthly - afterDiscount;
        const total = afterDiscount * tenureMonths;

        return {
            monthlyFee: rawMonthly,
            discountPercent: percent,
            discountLabel: label,
            discountAmount: discountAmount,
            payableMonthly: afterDiscount,
            totalContractValue: total,
            isValidConfig: true
        };
    }, [numJuniors, numSeniors, tenureMonths]);

    // Handlers
    const handleReset = () => {
        setNumJuniors(DEFAULTS.JUNIOR);
        setNumSeniors(DEFAULTS.SENIOR);
        setTenureMonths(DEFAULTS.TENURE);
    };

    const applySuggestion = (s) => {
        setNumJuniors(s.values.junior);
        setNumSeniors(s.values.senior);
        setTenureMonths(s.values.tenure);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(e.target);
        const payload = {
            companyName: formData.get('companyName'),
            name: formData.get('name'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            message: formData.get('notes') || 'No notes provided.',
            juniorCount: numJuniors,
            seniorCount: numSeniors,
            tenureMonths: tenureMonths,
            estimatedMonthly: formatCurrency(payableMonthly),
            estimatedTotal: formatCurrency(totalContractValue)
        };

        try {
            const response = await fetch('https://api.codemaker.com.my/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setSubmitSuccess(true);
            } else {
                alert('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Submission error:', error);
            alert('Network error. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSubmitSuccess(false);
    };

    return (
        <div className="pricing-page">
            <Helmet>
                <title>TaaS Pricing Calculator | CODEMAKER Technology</title>
                <meta name="description" content="Calculate your dedicated team costs. Transparent pricing with no hidden fees." />
            </Helmet>

            <header className="page-header">
                <div className="container">
                    <h1 className="brand">CODEMAKER <span className="brand-light">Technology</span></h1>
                    <p className="tagline">Transparent TaaS Pricing. No Hidden Costs.</p>
                </div>
            </header>

            <main className="container main-content">
                {/* Suggestions */}
                <section className="suggestions-section">
                    <h2 className="section-title">Suggested Configurations</h2>
                    <div className="suggestions-grid">
                        {SUGGESTIONS.map((s) => {
                            const isSelected =
                                numJuniors === s.values.junior &&
                                numSeniors === s.values.senior &&
                                tenureMonths === s.values.tenure;

                            return (
                                <button
                                    key={s.id}
                                    onClick={() => applySuggestion(s)}
                                    className={`suggestion-card ${isSelected ? 'highlight' : ''}`}
                                >
                                    <span className="suggestion-label">{s.label}</span>
                                    <div className="suggestion-details">
                                        <span>{s.values.junior} Jun</span>
                                        <span>{s.values.senior} Sen</span>
                                        <span>{s.values.tenure} Months</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Calculator Card */}
                <div className="calculator-card">
                    {/* Left: Inputs */}
                    <div className="calc-inputs">
                        <div className="calc-header-row">
                            <h3>Configure Your Team</h3>
                            <button onClick={handleReset} className="btn-text">Reset to Default</button>
                        </div>

                        {/* Junior Slider */}
                        <div className="slider-group">
                            <div className="slider-label-row">
                                <label>Junior Developers</label>
                                <span className="slider-value">{numJuniors}</span>
                            </div>
                            <input
                                type="range"
                                min="0" max="10" step="1"
                                value={numJuniors}
                                onChange={(e) => setNumJuniors(parseInt(e.target.value))}
                                className="range-input"
                                style={{ '--percent': `${(numJuniors / 10) * 100}%` }}
                            />
                            <div className="slider-limits">
                                <span>0</span>
                                <span>10</span>
                            </div>
                        </div>

                        {/* Senior Slider */}
                        <div className="slider-group">
                            <div className="slider-label-row">
                                <label>Senior Developers</label>
                                <span className="slider-value">{numSeniors}</span>
                            </div>
                            <input
                                type="range"
                                min="0" max="10" step="1"
                                value={numSeniors}
                                onChange={(e) => setNumSeniors(parseInt(e.target.value))}
                                className="range-input"
                                style={{ '--percent': `${(numSeniors / 10) * 100}%` }}
                            />
                            <div className="slider-limits">
                                <span>0</span>
                                <span>10</span>
                            </div>
                        </div>

                        {/* Tenure Slider */}
                        <div className="slider-group">
                            <div className="slider-label-row">
                                <label>Tenure (Months)</label>
                                <span className="slider-value">{tenureMonths}</span>
                            </div>
                            <input
                                type="range"
                                min="3" max="24" step="1"
                                value={tenureMonths}
                                onChange={(e) => setTenureMonths(parseInt(e.target.value))}
                                className="range-input"
                                style={{ '--percent': `${((tenureMonths - 3) / (24 - 3)) * 100}%` }}
                            />
                            <div className="slider-limits">
                                <span>3</span>
                                <span>24</span>
                            </div>
                            <div className="discount-badge-container">
                                {discountLabel && <span className="discount-badge">{discountLabel}</span>}
                            </div>
                        </div>

                        {!isValidConfig && (
                            <div className="inline-warning">
                                <WarningIcon />
                                <span>Please select at least 1 developer.</span>
                            </div>
                        )}
                    </div>

                    {/* Right: Results */}
                    <div className="calc-results">
                        <div className="result-row">
                            <span>Monthly Fee</span>
                            <span className="val-muted">{formatCurrency(monthlyFee)}</span>
                        </div>

                        <div className="result-row">
                            <span>Tenure Discount</span>
                            <span className="val-green">
                                {Math.round(discountPercent * 100)}%
                                {discountLabel && ` (${discountLabel})`}
                                {discountAmount > 0 && <span className="val-sub">(-{formatCurrency(discountAmount)})</span>}
                            </span>
                        </div>

                        <div className="divider"></div>

                        <div className="result-row highlight-row">
                            <span>Monthly Payable</span>
                            <span className="val-large">{formatCurrency(payableMonthly)}</span>
                        </div>

                        <div className="contract-box">
                            <span className="box-label">Total Contract Value</span>
                            <span className="box-val">{formatCurrency(totalContractValue)}</span>
                        </div>

                        <button
                            className="btn-primary"
                            disabled={!isValidConfig}
                            onClick={() => setShowModal(true)}
                        >
                            Get Proposal
                        </button>
                    </div>
                </div>

                {/* Comparison Panel (Anchoring) */}
                <section className="comparison-section">
                    <div className="comparison-grid">
                        {/* Internal Hiring */}
                        <div className="comparison-col internal">
                            <h4>Internal Hiring (Typical)</h4>
                            <p className="disclaimer">*Estimated costs based on market averages</p>
                            <ul className="comparison-list">
                                <li>Recruitment Fees (15-20%)</li>
                                <li>EPF & SOCSO (~13%)</li>
                                <li>Equipment & Software</li>
                                <li>Training & Onboarding Costs</li>
                                <li>Retraining if resignation occurs</li>
                            </ul>
                        </div>

                        {/* CODEMAKER */}
                        <div className="comparison-col codemaker">
                            <h4>CODEMAKER Managed Team</h4>
                            <div className="inc-badge">Everything Included</div>
                            <ul className="check-list">
                                {[
                                    "Managed Developers (Junior/Senior)",
                                    "Tech Lead Supervision Included",
                                    "QA Discipline Included",
                                    "Weekly Governance & Reporting",
                                    "Replacement Policy for Mismatch"
                                ].map((item, i) => (
                                    <li key={i}>
                                        <CheckIcon />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="page-footer">
                <div className="container">
                    <p>&copy; {new Date().getFullYear()} CODEMAKER Technology. All rights reserved.</p>
                </div>
            </footer>

            {/* Modal */}
            {showModal && (
                <div className="modal-backdrop" onClick={closeModal}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>
                            <CloseIcon />
                        </button>

                        {!submitSuccess ? (
                            <>
                                <h3 className="modal-title">Request Proposal</h3>
                                <div className="modal-summary">
                                    <strong>Summary:</strong> {numJuniors} Junior, {numSeniors} Senior, {tenureMonths} Months.
                                    <br />
                                    <strong>Total Value:</strong> {formatCurrency(totalContractValue)}
                                </div>
                                <form onSubmit={handleSubmit} className="modal-form">
                                    <label>Company Name <input required type="text" name="companyName" /></label>
                                    <label>Name <input required type="text" name="name" /></label>
                                    <label>Email <input required type="email" name="email" /></label>
                                    <label>Phone <input required type="tel" name="phone" /></label>
                                    <label>Notes (Optional) <textarea rows={2} name="notes"></textarea></label>

                                    <button type="submit" disabled={isSubmitting} className="btn-submit">
                                        {isSubmitting ? "Processing..." : "Submit Request"}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="success-message">
                                <div className="success-icon">
                                    <CheckIcon />
                                </div>
                                <h3>Request Sent!</h3>
                                <p>We have received your request for:</p>
                                <p className="success-summary">
                                    {numJuniors} Junior, {numSeniors} Senior for {tenureMonths} Months.
                                </p>
                                <p>A consultant will contact you within 24 hours.</p>
                                <button onClick={closeModal} className="btn-close-success">Close</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Pricing;
